import { observer } from 'mobx-react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import yup from '../../core/utils/yupValidate';
import { DATE_YMD_ISO, MSG, TRAINING_COURSE_SETTINGS_STATUS, TRAINING_COURSE_SETTINGS_TYPE } from '../../core/configs/constants';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo } from 'react';
import { useStore } from '../../core/utils/hook';
import { flowResult } from 'mobx';
import { CalendarCustom, ReactNotifications } from '../../components';
import moment from 'moment';

const SettingTrainingCourseModal = observer(({ data, fetchData, id }) => {
    // store
    const {
        modalStore: { hide },
        courseStore: { updateSettingsTrainingCourseForGA }
    } = useStore();

    // form
    const validateSchema = yup.object().shape({
        submitReportStatus: yup.boolean(),
        submitReportDuration: yup.mixed().when('submitReportStatus', {
            is: true,
            then: yup.number()
                .transform((value, originalValue) => originalValue === '' ? undefined : value)
                .required(MSG['error.required']),
            otherwise: yup.string().nullable().notRequired()
        }),
        reportMaxLength: yup.mixed().when('submitReportStatus', {
            is: true,
            then: yup.number()
                .transform((value, originalValue) => originalValue === '' ? undefined : value)
                .required(MSG['error.required']),
            otherwise: yup.string().nullable().notRequired()
        }),
        submitReportType: yup.mixed().when('submitReportStatus', {
            is: true,
            then: yup.string().required(MSG['error.required']),
            otherwise: yup.string().nullable().notRequired()
        }),
        submitQuestionStatus: yup.boolean(),
        submitQuestionDuration: yup.mixed().when('submitQuestionStatus', {
            is: true,
            then: yup.number()
                .transform((value, originalValue) => originalValue === '' ? undefined : value)
                .required(MSG['error.required']),
            otherwise: yup.string().nullable().notRequired()
        }),
        submitQuestionType: yup.mixed().when('submitQuestionStatus', {
            is: true,
            then: yup.string().required(MSG['error.required']),
            otherwise: yup.string().nullable().notRequired()
        }),
        incentivePeriodStatus: yup.boolean(),
        incentivePeriod: yup.mixed().when('incentivePeriodStatus', {
            is: true,
            then: yup.string().required(MSG['error.required']).nullable().typeError(MSG['error.required']),
            otherwise: yup.string().nullable().notRequired()
        })
    });
    

    const {
        register,
        handleSubmit,
        formState: {
            errors,
            isSubmitting
        },
        reset,
        setValue,
        watch,
        getValues,
        trigger
    } = useForm({resolver: yupResolver(validateSchema), mode: 'onChange'});

    const watchSubmitReportType = watch('submitReportType');
    const watchSubmitQuestionType = watch('submitQuestionType');
    const watchIncentivePeriod = watch('incentivePeriod');

    const rangeSubmitReportDuration = useMemo(() => {
        if (Number(watchSubmitReportType) === TRAINING_COURSE_SETTINGS_TYPE.DAY) {
            return {
                min: 1,
                max: 365
            }
        } else if (Number(watchSubmitReportType) === TRAINING_COURSE_SETTINGS_TYPE.MONTH) {
            return {
                min: 1,
                max: 12
            }
        }
    }, [watchSubmitReportType]);

    const rangeSubmitQuestionDuration = useMemo(() => {
        if (Number(watchSubmitQuestionType) === TRAINING_COURSE_SETTINGS_TYPE.DAY) {
            return {
                min: 1,
                max: 365
            }
        } else if (Number(watchSubmitQuestionType) === TRAINING_COURSE_SETTINGS_TYPE.MONTH) {
            return {
                min: 1,
                max: 12
            }
        }
    }, [watchSubmitQuestionType]);

    // lifecycle
    useEffect(() => {
       
        if (data) {
            reset({
                ...data,
                submitReportStatus: data.submitReportStatus === TRAINING_COURSE_SETTINGS_STATUS.ON,
                submitQuestionStatus: data.submitQuestionStatus === TRAINING_COURSE_SETTINGS_STATUS.ON,
                incentivePeriodStatus: data.incentivePeriod ? true : false,
                reportMaxLength: data.reportMaxLength || 0
            });
        }
    }, [data])

    const handleSubmitReportDurationChange = (e) => {
        const inputValue = e.target.value;

        const numericValue = Number(inputValue);
        
        if (inputValue === '' || (numericValue >= rangeSubmitReportDuration.min && numericValue <= rangeSubmitReportDuration.max)) {
            setValue('submitReportDuration', inputValue, { shouldValidate: true });
        }
    };

    const handleSubmitQuestionDurationChange = (e) => {
        const inputValue = e.target.value;

        if (!/^-?\d*$/.test(inputValue)) return;

        const numericValue = Number(inputValue);

        if (inputValue === '' || (numericValue >= rangeSubmitQuestionDuration.min && numericValue <= rangeSubmitQuestionDuration.max)) {
            setValue('submitQuestionDuration', inputValue, { shouldValidate: true });
        }
    };

    const handleReportMaxLengthChange = (e) => {
        const inputValue = e.target.value;

        if (!/^-?\d*$/.test(inputValue)) return;

        const numericValue = Number(inputValue);

        if (inputValue === '' || (numericValue >= 1)) {
            setValue('reportMaxLength', inputValue, { shouldValidate: true });
        }
    };



    // function
    const onSubmit = async (data) => {
        const {
            submitReportStatus,
            submitReportType,
            submitReportDuration,
            submitQuestionStatus,
            submitQuestionType,
            submitQuestionDuration,
            incentivePeriodStatus,
            incentivePeriod,
            reportMaxLength
        } = data;
        const payload = {
            submitReportStatus: submitReportStatus ? TRAINING_COURSE_SETTINGS_STATUS.ON : TRAINING_COURSE_SETTINGS_STATUS.OFF,
            submitQuestionStatus: submitQuestionStatus ? TRAINING_COURSE_SETTINGS_STATUS.ON : TRAINING_COURSE_SETTINGS_STATUS.OFF,
            submitReportType: Number(submitReportType),
            submitReportDuration: Number(submitReportDuration),
            submitQuestionType: Number(submitQuestionType),
            submitQuestionDuration: Number(submitQuestionDuration),
            reportMaxLength: Number(reportMaxLength),
            ...(incentivePeriodStatus ? { incentivePeriod } : {})
        }
        const result = await flowResult(updateSettingsTrainingCourseForGA(id, payload));
        if (result) {
            hide();
            fetchData && fetchData();
            ReactNotifications('success', MSG['inform.success.update']);
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className='setting-training-course-modal'>
            <div className='setting-training-course-content'>
                <div className='setting-training-course-item'>
                    <div className='header'>
                        <div className='header-title'>受講推奨期日</div>
                        <SwitchPrimitives.Root
                            className='custom-switch'
                            checked={getValues('incentivePeriodStatus')}
                            onCheckedChange={(checked) => setValue('incentivePeriodStatus', checked, { shouldValidate: true })}>
                            <SwitchPrimitives.Thumb className='custom-toggle-thumb'/>
                        </SwitchPrimitives.Root>
                    </div>
                    <div className='desc'>受講者が研修指定講座の受講を完了することが推奨される時点。</div>
                    {
                        getValues('incentivePeriodStatus') && (
                            <div className='settings row'>
                                <label className='settings-title col-2 field-required'>受講期日</label>
                                <div className='settings-input col-7 row'>
                                    <div className='col-11 pd-0'>
                                        <CalendarCustom
                                            className='height-35'
                                            date={watchIncentivePeriod}
                                            displayMode={'date'}
                                            onChange={date => {
                                                setValue('incentivePeriod', date ? moment(date).format(DATE_YMD_ISO) : null);
                                                trigger('incentivePeriod');
                                            }}
                                        />
                                        {
                                            errors.incentivePeriod &&
                                            <div className='text-danger fs-error mg-t-5 pd-0' style={{ fontSize: '11px' }}>
                                                {errors.incentivePeriod?.message}</div>
                                        }
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </div>
                <div className='setting-training-course-item mg-t-15'>
                    <div className='header'>
                        <div className='header-title'>提出物</div>
                        <SwitchPrimitives.Root
                            className='custom-switch'
                            checked={getValues('submitReportStatus')}
                            onCheckedChange={(checked) => setValue('submitReportStatus', checked, { shouldValidate: true })}>
                            <SwitchPrimitives.Thumb className='custom-toggle-thumb'/>
                        </SwitchPrimitives.Root>
                    </div>
                    <div className='desc'>受講者が受講完了後に提出物を提出できるようにします。</div>
                    {
                        getValues('submitReportStatus') && (
                            <div>
                                <div className='settings row'>
                                    {/* submit report duration */}
                                    <label className='settings-title col-2 field-required'>提出期限</label>
                                    <div className='settings-input col-7 row'>
                                        <div className='col-6'>
                                            <input
                                                type="number"
                                                value={getValues('submitReportDuration')}
                                                onChange={(e) => handleSubmitReportDurationChange(e)}
                                                className="border p-2 rounded-md no-spinner"
                                                style={{ width: '100%' }}
                                            />
                                            {
                                                errors?.submitReportDuration &&
                                            <div
                                                className='text-danger mg-t-5'
                                                style={{ fontSize: '12px' }}>{errors.submitReportDuration?.message}</div>
                                            }
                                        </div>
                                        <select {...register('submitReportType', {
                                            onChange: (e) => {
                                                const value = e.target.value;
                                                if (Number(value) === TRAINING_COURSE_SETTINGS_TYPE.DAY &&
                                                getValues('submitReportDuration') > 365) {
                                                    setValue('submitReportDuration', '', { shouldValidate: true })
                                                } else if (Number(value) === TRAINING_COURSE_SETTINGS_TYPE.MONTH &&
                                                getValues('submitReportDuration') > 12) {
                                                    setValue('submitReportDuration', '', { shouldValidate: true })
                                                }
                                                setValue('submitReportType', value);
                                            }
                                        })} className='col-5'>
                                            <option value={TRAINING_COURSE_SETTINGS_TYPE.MONTH}>ヶ月</option>
                                            <option value={TRAINING_COURSE_SETTINGS_TYPE.DAY}>日</option>
                                        </select>
                                    </div>
                                    <div className='settings-desc col-3'>（受講完了日から）</div>
                                </div>
                                <div className='settings row' style={{ display: 'flex', alignItems: 'center' }}>
                                    <label className='settings-title col-2 field-required'>入力可能な最大文字数</label>
                                    <div className='settings-input col-7 row'>
                                        <div className='col-6'>
                                            <input
                                                type="text"
                                                value={getValues('reportMaxLength')}
                                                min={1}
                                                // max={1000}
                                                onChange={(e) => handleReportMaxLengthChange(e)}
                                                className="border p-2 rounded-md no-spinner"
                                                style={{ width: '100%' }}
                                            />
                                            {
                                                errors?.reportMaxLength &&
                                            <div
                                                className='text-danger mg-t-5'
                                                style={{ fontSize: '12px' }}>{errors.reportMaxLength?.message}</div>
                                            }
                                        </div>
                                        
                                    </div>
                                    
                                </div>
                            </div>
                        )
                    }
                </div>
                <div className='setting-training-course-item mg-t-15'>
                    <div className='header'>
                        <div className='header-title'>質問</div>
                        <SwitchPrimitives.Root
                            className='custom-switch'
                            checked={getValues('submitQuestionStatus')}
                            onCheckedChange={(checked) => setValue('submitQuestionStatus', checked, { shouldValidate: true })}>
                            <SwitchPrimitives.Thumb className='custom-toggle-thumb'/>
                        </SwitchPrimitives.Root>
                    </div>
                    <div className='desc'>受講者が受講完了後に講座内容について質問できるようにします。</div>
                    {
                        getValues('submitQuestionStatus') && (
                            <div className='settings row'>
                                <label className='settings-title col-2 field-required'>質問可能な期限</label>
                                <div className='settings-input col-7 row'>
                                    
                                    <div className='col-6'>
                                        <input
                                            type="number"
                                            value={getValues('submitQuestionDuration')}
                                            onChange={(e) => handleSubmitQuestionDurationChange(e)}
                                            className="border p-2 rounded-md no-spinner"
                                            style={{ width: '100%' }}
                                            disabled
                                        />
                                        {
                                            errors?.submitQuestionDuration &&
                                            <div
                                                className='text-danger mg-t-5'
                                                style={{ fontSize: '12px' }}>{errors.submitQuestionDuration?.message}</div>
                                        }
                                    </div>
                                    <select {...register('submitQuestionType', {
                                        onChange: (e) => {
                                            const value = e.target.value;
                                            if (Number(value) === TRAINING_COURSE_SETTINGS_TYPE.DAY &&
                                                getValues('submitQuestionDuration') > 365) {
                                                setValue('submitQuestionDuration', '', { shouldValidate: true })
                                            } else if (Number(value) === TRAINING_COURSE_SETTINGS_TYPE.MONTH &&
                                                getValues('submitQuestionDuration') > 12) {
                                                setValue('submitQuestionDuration', '', { shouldValidate: true })
                                            }
                                            setValue('submitQuestionType', value);
                                        }
                                    })} className='col-5 disabled' disabled>
                                        <option value={TRAINING_COURSE_SETTINGS_TYPE.MONTH}>ヶ月</option>
                                        <option value={TRAINING_COURSE_SETTINGS_TYPE.DAY}>日</option>
                                    </select>
                                </div>
                                <div className='settings-desc col-3'>（受講完了日から）</div>
                            </div>
                        )
                    }
                </div>
            </div>
            <div className='d-flex justify-content-center align-items-center flex-gap-30 pd-tb-10'
                style={{ borderTop: '1px solid #d3d3d3' }}>
                <button type='button' className='btn btn-default-1 cancel-button min-width-120' onClick={() => hide()}>キャンセル</button>
                <button 
                    type='submit'
                    disabled={isSubmitting || typeof getValues('report') === 'string'}
                    className='btn btn-default-3 min-width-120'>
                    保存
                </button>
            </div>
        </form>
    )
});

export default SettingTrainingCourseModal;